import React from 'react';
import { Flex, Heading, Text } from 'theme-ui';
import QuickshopColorSwatch from './ColorSwatch';
import { BundleSpotlight, KitSpotlight, ProductSpotlight, isProductSpotlight } from '../utils';

type Props = {
  spotlight: ProductSpotlight | BundleSpotlight | KitSpotlight;
  handleColorSwatchClick?: (optionValue: Queries.OptionValuesFragmentFragment) => void;
  selectedColor?: Queries.OptionValuesFragmentFragment | null;
  isTrayOpen?: boolean;
  price: number | null | undefined;
  regularPrice: number | null | undefined;
};

const CardContent = ({ spotlight, handleColorSwatchClick, selectedColor, isTrayOpen, regularPrice, price }: Props) => {
  const name = isProductSpotlight(spotlight) ? spotlight.content.longTitle : spotlight.content.name;

  const getProductNames = (spotlight: BundleSpotlight | KitSpotlight) => {
    const productNames = spotlight?.content?.products?.map((product) => product?.name);
    const uniqueProductNames = [...new Set(productNames)];
    if (uniqueProductNames.length === 1) return uniqueProductNames.join('');
    return uniqueProductNames.join(', ');
  };

  return (
    <>
      <Flex
        sx={{
          py: '0.8rem',
          gridArea: 'swatch',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {isProductSpotlight(spotlight) ? (
          <QuickshopColorSwatch
            product={spotlight.content}
            handleColorSwatchClick={handleColorSwatchClick}
            selectedColor={selectedColor}
            forwardSx={{ gap: '2xs', '--dots': '1.6rem', flexWrap: 'wrap' }}
          />
        ) : (
          <Text
            as="p"
            sx={{
              fontSize: '1.6rem',
              lineHeight: 1.5,
              gridArea: 'microload',
            }}
          >
            {getProductNames(spotlight)}
          </Text>
        )}
        {regularPrice !== price ? (
          <Text
            sx={{
              fontSize: '1.6rem',
              lineHeight: 1,
              fontWeight: 400,
              color: 'UIGray2',
              textDecoration: 'line-through',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            $ {regularPrice}
          </Text>
        ) : null}
      </Flex>
      <Flex sx={{ justifyContent: 'space-between', gridArea: 'name' }}>
        {name ? <Heading as="h4" variant="text.h7" dangerouslySetInnerHTML={{ __html: name }} /> : null}

        <Text
          sx={{
            fontSize: '1.8rem',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          $ {price}
        </Text>
      </Flex>
      <Text
        as="p"
        sx={{
          fontSize: '1.6rem',
          lineHeight: 1.5,
          gridArea: 'microload',
        }}
      >
        {spotlight.cardMicroloadHighlight}
      </Text>
      {spotlight.quickshopCardCopy?.childMarkdownRemark?.html ? (
        <Text
          variant="text.paragraphSmall"
          style={{
            opacity: isTrayOpen ? 0.25 : 1,
            transition: 'opacity 0.2s ease-in-out',
            willChange: 'opacity',
          }}
          sx={{ gridArea: 'description' }}
          dangerouslySetInnerHTML={{ __html: spotlight.quickshopCardCopy?.childMarkdownRemark?.html }}
        />
      ) : (
        <Text variant="text.paragraphSmall" sx={{ gridArea: 'description' }}>
          {spotlight.cdpCopy}
        </Text>
      )}
    </>
  );
};

export default CardContent;
